import { Injectable } from '@angular/core';
import { CurrentUser } from '@sondermind/data-access/client-user';
import { IChatRoom } from '@sondermind/utilities/models-chat';
import { MatchAssignmentType } from '@sondermind/utilities/models-matching';
import {
  ITherapistMatch, ITherapistTemplate, MatchCategory, ProviderMatchStatus,
  RequestedSessionType
} from '@sondermind/utilities/models-therapist';

/**
 * Provides basic logic operations on ITherapistTemplate objects to get attributes of a therapist,
 * e.g. 'isScheduled', 'isDeclined'
 */
@Injectable({
  providedIn: 'root'
})
export class TherapistLogicService {
  /**
   * Check if therapist has been SCHEDULED on NCR.
   *
   * From the Swagger documentation:
   *
   *  Indicates whether or not the current client is in a "scheduled" state with this provider.
   *  A client is in the "scheduled" state if any of the following conditions are true:
   *    - The client was matched with the provider and is now scheduled. (crm.aasm_state == "scheduled")
   *    - The client was invited by the provider and is now active. (patient.invite_sent_at && patient.active)
   *    - The client has any upcoming scheduled sessions with the provider.
   */
  isScheduled(therapist: ITherapistTemplate): boolean {
    return therapist.inScheduledState;
  }

  /**
   * Check if therapist has DECLINED on any NCR.
   */
  isDeclined(therapist: ITherapistTemplate): boolean {
    return therapist.matches?.some((match) => match.status === ProviderMatchStatus.DECLINED);
  }

  /**
   * Check if therapist has ACCEPTED on an NCR.
   */
  isAccepted(therapist: ITherapistTemplate): boolean {
    return therapist.matches?.some((match) => match.status === ProviderMatchStatus.ACCEPTED);
  }

  /**
   * Check if therapist has NOTIFIED on an NCR.
   */
  isNotified(therapist: ITherapistTemplate): boolean {
    return therapist.matches?.some((match) => match.status === ProviderMatchStatus.NOTIFIED);
  }

  /**
   * Check if therapist is matched but not yet scheduled via either NOTIFIED, NOTIFICATION_DELAYED, or ACCEPTED status.
   */
  isActionable(therapist: ITherapistTemplate): boolean {
    return therapist.matches?.some((match) => match.actionable);
  }

  isOnHoldMatch(therapist: ITherapistTemplate): boolean {
    return therapist.matches?.some((match) => match.status === ProviderMatchStatus.ON_HOLD);
  }

  isAdditionalMatch(therapist: ITherapistTemplate): boolean {
    return therapist.matches?.some((match) => match.additional_match);
  }

  isMemberSelectedMatch(therapist: ITherapistTemplate): boolean {
    return therapist.matches?.some((match) => match.status === ProviderMatchStatus.MEMBER_SELECTED);
  }

  /**
   * True if the following are both true:
   * 1. The client has a Request to Schedule with a therapist, or has Proposed a Time
   * 2. The therapist is either in the Notified/Accepted states, or a free consultation has been requested
   */
  isTherapistPostSessionRequestState(therapist: ITherapistTemplate): boolean {
    return ((this.hasRTSSessionRequested(therapist) || this.hasExistingPatRequest(therapist)) &&
      (this.isNotified(therapist) || this.isAccepted(therapist))) || this.hasConsultationRequested(therapist);
  }

  /**
   * True if the therapist is in the Accepted state and either there is a Request to Schedule
   * or a free consultation requested
   */
  isAcceptedAndRequestedTherapist(therapist: ITherapistTemplate): boolean {
    return this.isAccepted(therapist)
      && (this.hasRTSSessionRequested(therapist)
        || this.hasConsultationRequested(therapist));
  }

  /**
   * Returns true if one of the following 2 conditions is true:
   * ***********
   * 1.
   *     a. The provider has either a RTS Request to Schedule, or a PAT Propose a Time request
   *     b. The provider is neither in the Accepted nor Declined state
   * ***********
   * 2.
   *     a. The provider has a request for a free consultation
   *     b. The provider does not have direct scheduling enabled
   *     c. The provider is not in the Declined state
   */
  isPendingPatRtsOrConsult(therapist: ITherapistTemplate): boolean {
    return ((this.hasRTSSessionRequested(therapist) || this.hasExistingPatRequest(therapist))
        && !this.isAccepted(therapist)
        && !this.isDeclined(therapist))
      || (this.hasConsultationRequested(therapist)
        && !this.isDirectScheduleEnabled(therapist)
        && !this.isDeclined(therapist));
  }

  /**
   * Returns the ITherapistMatch with a matching referralId, if it exists, or undefined
   */
  getMatch(therapist: ITherapistTemplate, referralId: number): ITherapistMatch | undefined {
    return therapist?.matches?.find((x) => x.referral_id === referralId);
  }

  /**
   * Determines which MatchCategory the client's match with the therapist is
   */
  getCategoryFromMatch(match: ITherapistMatch): MatchCategory {
    const isAssignmentTopRecommended = match.match_assignment === MatchAssignmentType.TOP_RECOMMENDED
      || match.match_assignment === MatchAssignmentType.RUNNER_UP;

    if (isAssignmentTopRecommended) return MatchCategory.TOP_RECOMMENDED;
    return MatchCategory.OTHER_TOP_SCORERS;
  }

  hasClientSentMessageToTherapist(match: ITherapistMatch): boolean {
    return match.client_sent_ncc;
  }

  /**
   * If a match has an aasm_state IN ('notified', 'accepted') or the therapist has sent a message to the client,
   * then the therapist is in the providers who will reach out category
   */
  willProviderReachOut(therapist: ITherapistTemplate, chatRooms: IChatRoom[], match: ITherapistMatch): boolean {
    const therapistParticipant = chatRooms
      .map((chatRoom) => chatRoom.participants)
      .flat()
      .find((participant) => participant.platformIdentity === therapist.platformIdentity);

    const hasTherapistSentMessage = !!therapistParticipant?.lastPublishedAt;

    return hasTherapistSentMessage
      || match.status === ProviderMatchStatus.NOTIFIED
      || match.status === ProviderMatchStatus.ACCEPTED;
  }

  /**
   * Check if therapist has any session requests from the client made via RTS (Request to Schedule)
   */
  hasRTSSessionRequested(therapist: ITherapistTemplate): boolean {
    return therapist.matches?.some((match) => match.session_requested && match.requested_session_type === RequestedSessionType.SESSION);
  }

  /**
   * Check if the therapist has a free consultation requested by the client.
   */
  hasConsultationRequested(therapist: ITherapistTemplate): boolean {
    return therapist.matches?.some((match) => match.session_requested && match.requested_session_type === RequestedSessionType.FREE_CONSULTATION);
  }

  /**
   * Check if therapist has existing propose a time request.
   */
  hasExistingPatRequest(therapist: ITherapistTemplate): boolean {
    return therapist.hasPendingPatSession;
  }

  /**
   * Check if therapist has propose-a-time enabled
   */
  hasProposeATimeEnabled(therapist: ITherapistTemplate): boolean {
    return therapist.proposeATimeEnabled;
  }

  /**
   * Check if therapist has been automatched or manual matched
   * @param therapist
   * @returns true if manual match, false if automatch
   */
  isManualMatched(therapist: ITherapistTemplate): boolean {
    return therapist.matches.some((match) => !match.auto_matched);
  }

  /**
   * Check if therapist offers direct scheduling
   * @param therapist
   * @returns true if direct schedule is enabled and therapist has accepted the match
   */
  isDirectScheduleEnabled(therapist: ITherapistTemplate): boolean {
    return therapist.directSchedulingEnabled
      && therapist.matches.some((match) => match.status === ProviderMatchStatus.ACCEPTED);
  }

  /**
   * Check if therapist and client relationship is established
   * @param therapist
   * @returns true if therapist status is ACCEPTED or SCHEDULED or if client is provider-invited
   */
  checkTherapistRelationshipEstablished(therapist: ITherapistTemplate): boolean {
    // isScheduled checks for either SCHEDULED status or if client is providerInvited
    return this.isScheduled(therapist) || this.isAccepted(therapist);
  }

  /**
   * @param therapistList
   * @returns if any provider was manually matched, return true
   * else return false
   */
  checkManualMatchedStatus(therapistList: ITherapistTemplate[]): boolean {
    return therapistList.some((therapist) => this.isManualMatched(therapist));
  }

  /**
   * If any provider is SCHEDULED, return true.
   */
  checkScheduledStatus(therapistList: ITherapistTemplate[]): boolean {
    return therapistList.some((t) => this.isScheduled(t));
  }

  /**
   * If there are no providers, assumption is that they are on initial NCR.
   * If any provider is not SCHEDULED, this means there is an open NCR.
   */
  checkEmptyOrActionable(therapistList: ITherapistTemplate[]): boolean {
    return therapistList.length === 0 || therapistList.some((t) => this.isActionable(t));
  }

  /**
   * Check if any therapists in list have matches of a status the client
   * can view for an open NCRs.
   */
  checkNoMatchesYet(therapistList: ITherapistTemplate[]): boolean {
    return !therapistList.some((t: ITherapistTemplate) => this.isDeclined(t) || this.isActionable(t));
  }

  /**
   * Check if all therapists on open NCRs have declined.
   */
  checkDeclinedStatus(therapistList: ITherapistTemplate[]): boolean {
    const declinedList = therapistList.filter((t: ITherapistTemplate) => this.isDeclined(t));

    return declinedList.length > 0
      && declinedList.length === therapistList.filter((t) => !this.isScheduled(t)).length;
  }

  /**
   * Check if one therapists on open NCRs have declined.
   */
  checkOneOfManyDeclinedStatus(therapistList: ITherapistTemplate[]): boolean {
    const declinedList = therapistList.filter((t: ITherapistTemplate) => this.isDeclined(t));
    return declinedList.length > 0 && therapistList.length > declinedList.length;
  }

  /**
   * Check if all therapists are direct schedule enabled.
   */
  checkAllDirectScheduleEnabled(therapistList: ITherapistTemplate[]): boolean {
    const directScheduleList = therapistList.filter((t: ITherapistTemplate) => this.isDirectScheduleEnabled(t));

    return directScheduleList.length > 0
      && directScheduleList.length === therapistList.filter((t) => !this.isScheduled(t)).length;
  }

  /**
   * Check if no therapists are direct schedule enabled.
   */
  checkNoDirectScheduleEnabled(therapistList: ITherapistTemplate[]): boolean {
    const directScheduleList = therapistList.filter((t: ITherapistTemplate) => this.isDirectScheduleEnabled(t));
    return directScheduleList.length === 0;
  }

  /**
   * Following scenarios for Request to Schedule
   * therapist is notified and does not have Direct Scheduling -> show RTS
   * therapist is notified and has DS -> show RTS
   * therapist is accepted and does not have DS -> show RTS
   * therapist is accepted and has DS -> do not show RTS
   * therapist is scheduled -> do not show RTS
   *
   */
  canShowRequestToSchedule(therapist: ITherapistTemplate, currentUser: CurrentUser): boolean {
    const isDirectScheduleEnabled = therapist.directSchedulingEnabled && currentUser?.user?.allowScheduling;

    return !this.checkTherapistRelationshipEstablished(therapist)
      || (therapist.proposeATimeForInvitedClientsFeatureFlag && currentUser.user?.providerInvited && !isDirectScheduleEnabled)
      || (this.isAccepted(therapist) && !isDirectScheduleEnabled)
      || therapist.hasPendingPatSession;
  }

  /**
   * Check if therapist has been expired from all related NCRs.
   * If there are no matches, provider had invited client and is not on any NCR.
   */
  isFullyExpired(therapist: ITherapistTemplate): boolean {
    if (therapist.matches?.length > 0) {
      return !therapist.matches.some((m) => m.status !== ProviderMatchStatus.EXPIRED);
    }

    return false;
  }

  /**
   * Check number of matches in either NOTIFIED or ACCEPTED status.
   */
  getNumberOfActiveMatches(therapistList: ITherapistTemplate[]): number {
    return therapistList.filter((t) => this.isAccepted(t) || this.isNotified(t)).length;
  }
}
